import PDFBuilder, { fmtDate, fmtDateFromISO } from "../../utils/pdfBuilder.js";

const scopLabels = {
  personal: "necesitati personale",
  medical: "cheltuieli medicale",
  studii: "studii / educatie",
  afacere: "investitie / afacere",
  altele: "scopuri personale",
};

const indiceLabels = {
  IRCC: "IRCC",
  EURIBOR_3M: "EURIBOR 3M",
  EURIBOR_6M: "EURIBOR 6M",
  SOFR_3M: "SOFR 3M",
};

export default function buildImprumutPDF(data, filename) {
  const pdf = new PDFBuilder();
  const today = fmtDate(new Date());

  const dataImprumutFmt = data.dataImprumut
    ? fmtDateFromISO(data.dataImprumut)
    : "___";

  const suma = data.sumaImprumut ? Number(data.sumaImprumut) : 0;
  const valuta = data.valuta || "RON";
  const nrRate = parseInt(data.nrRate) || 12;
  const sumaRata = data.modalitateRestituire === "rate" && suma > 0
    ? (suma / nrRate).toFixed(2)
    : null;

  // Genereaza datele scadente (ultima zi a fiecarei luni)
  const genereazaScadente = () => {
    if (!data.dataImprumut) return [];
    const startDate = new Date(data.dataImprumut);
    const scadente = [];
    for (let i = 1; i <= nrRate; i++) {
      const year = startDate.getFullYear();
      const month = startDate.getMonth() + i;
      const ultimaZi = new Date(year, month + 1, 0);
      scadente.push(fmtDate(ultimaZi));
    }
    return scadente;
  };
  const dateScadente = genereazaScadente();

  // Data restituirii: pentru rate = ultima scadenta, pentru integral = data din formular
  const dataRestituireFmt = data.modalitateRestituire === "rate"
    ? (dateScadente.length > 0 ? dateScadente[dateScadente.length - 1] : "___")
    : (data.dataRestituire ? fmtDateFromISO(data.dataRestituire) : "___");

  // Dynamic section numbering based on garantieActiva
  const hasGarantie = !!data.garantieActiva;
  const sObligatii = hasGarantie ? "VIII" : "VII";

  const sIncetare = hasGarantie ? "IX" : "VIII";
  const nIncetare = hasGarantie ? "9" : "8";
  const sLitigii = hasGarantie ? "X" : "IX";
  const nLitigii = hasGarantie ? "10" : "9";
  const sFinale = hasGarantie ? "XI" : "X";
  const nFinale = hasGarantie ? "11" : "10";

  // Header
  pdf.title("Contract de Imprumut", "(intre persoane fizice / juridice)");

  pdf.p(`Nr. ___ din ${today}`, { indent: 0 });

  // I. PARTILE CONTRACTANTE
  pdf.section("I. PARTILE CONTRACTANTE");

  pdf.pMixed([{ text: "1.1. Imprumutatorul:", bold: true }]);
  pdf.renderPartyPFPJ(data, "imprumutator", data.imprumutatorPJ);

  pdf.italic("si");

  pdf.pMixed([{ text: "1.2. Imprumutatul:", bold: true }]);
  pdf.renderPartyPFPJ(data, "imprumutat", data.imprumutatPJ);

  pdf.intro("au convenit incheierea prezentului contract de imprumut, in conformitate cu dispozitiile art. 2158-2170 din Codul Civil, in urmatoarele conditii:");

  // II. OBIECTUL CONTRACTULUI
  pdf.section("II. OBIECTUL CONTRACTULUI");

  pdf.pMixed([
    { text: "2.1. ", bold: true },
    { text: "Imprumutatorul acorda Imprumutatului un imprumut in suma de " },
    { text: `${suma > 0 ? suma.toLocaleString("ro-RO") : "___"} ${valuta}`, bold: true },
    { text: ". Suma imprumutata este destinata utilizarii de catre Imprumutat pentru scopul declarat: " },
    { text: scopLabels[data.scopImprumut] || "necesitati personale", bold: true },
    { text: ", fara a afecta obligatia de restituire a sumei." },
  ]);

  pdf.pMixed([
    { text: "2.2. ", bold: true },
    { text: "Suma imprumutata va fi remisa Imprumutatului in numerar / prin transfer bancar, la data semnarii prezentului contract sau la data de " },
    { text: dataImprumutFmt, bold: true },
    { text: "." },
  ]);

  pdf.article("2.3.", "Imprumutatul confirma primirea sumei mentionate la art. 2.1 si se obliga sa o restituie in conditiile prevazute in prezentul contract.");

  // III. DURATA CONTRACTULUI
  pdf.section("III. DURATA CONTRACTULUI");

  if (data.modalitateRestituire === "rate") {
    pdf.pMixed([
      { text: "3.1. ", bold: true },
      { text: "Imprumutul se acorda incepand cu data de " },
      { text: dataImprumutFmt, bold: true },
      { text: " si trebuie restituit in " },
      { text: `${nrRate} rate lunare`, bold: true },
      { text: ", conform prevederilor art. V." },
    ]);
  } else {
    pdf.pMixed([
      { text: "3.1. ", bold: true },
      { text: "Imprumutul se acorda incepand cu data de " },
      { text: dataImprumutFmt, bold: true },
      { text: " si trebuie restituit integral pana la data de " },
      { text: dataRestituireFmt, bold: true },
      { text: "." },
    ]);
  }

  pdf.article("3.2.", "Imprumutatul poate restitui suma imprumutata si inainte de termenul prevazut, fara penalitati de rambursare anticipata.");

  // IV. DOBANDA
  pdf.section("IV. DOBANDA");

  if (data.tipDobanda === "fara") {
    pdf.article("4.1.", "Prezentul imprumut este acordat fara dobanda, cu titlu gratuit.");
  }

  if (data.tipDobanda === "fixa") {
    pdf.pMixed([
      { text: "4.1. ", bold: true },
      { text: "Imprumutul este purtator de dobanda " },
      { text: "fixa", bold: true },
      { text: " in cuantum de " },
      { text: `${data.dobandaProcent || "___"}% pe an`, bold: true },
      { text: "." },
    ]);

    pdf.pMixed([
      { text: "4.2. ", bold: true },
      { text: `Dobanda se calculeaza de la data acordarii imprumutului si se achita ${data.modalitateRestituire === "rate" ? "lunar, odata cu rata" : "la data restituirii integrale a sumei imprumutate"}.` },
    ]);
  }

  if (data.tipDobanda === "variabila") {
    const indiceLabel = indiceLabels[data.dobandaIndice] || "IRCC";
    const bancaRef = data.valuta === "EUR"
      ? "Banca Centrala Europeana"
      : data.valuta === "USD"
        ? "Federal Reserve"
        : "Banca Nationala a Romaniei";

    pdf.pMixed([
      { text: "4.1. ", bold: true },
      { text: "Imprumutul este purtator de dobanda " },
      { text: "variabila", bold: true },
      { text: ", calculata dupa formula: " },
      { text: `${indiceLabel} + ${data.dobandaMarja || "___"}% marja fixa`, bold: true },
      { text: "." },
    ]);

    pdf.pMixed([
      { text: "4.2. ", bold: true },
      { text: "Indicele de referinta " },
      { text: indiceLabel, bold: true },
      { text: ` este cel publicat de ${bancaRef} la data scadentei fiecarei plati.` },
    ]);

    pdf.pMixed([
      { text: "4.3. ", bold: true },
      { text: "Dobanda totala (indice + marja) nu poate depasi plafonul maxim de " },
      { text: `${data.dobandaPlafon || "___"}% pe an`, bold: true },
      { text: ", indiferent de evolutia indicelui de referinta." },
    ]);

    pdf.pMixed([
      { text: "4.4. ", bold: true },
      { text: `Dobanda se calculeaza asupra soldului restant si se achita ${data.modalitateRestituire === "rate" ? "concomitent cu fiecare rata, in cuantumul rezultat conform prezentului articol" : "la data restituirii integrale a sumei imprumutate, calculata pentru intreaga perioada"}.` },
    ]);
  }

  // V. RESTITUIREA IMPRUMUTULUI
  pdf.section("V. RESTITUIREA IMPRUMUTULUI");

  if (data.modalitateRestituire === "rate") {
    pdf.pMixed([
      { text: "5.1. ", bold: true },
      { text: "Restituirea sumei imprumutate se va face in " },
      { text: `${nrRate} rate lunare egale`, bold: true },
      { text: ", in cuantum de " },
      { text: `${sumaRata ? `${sumaRata} ${valuta}` : "___"} / rata`, bold: true },
      { text: " (fara dobanda)." },
    ]);

    pdf.pMixed([
      { text: "5.2. ", bold: true },
      { text: "Ratele sunt scadente in ultima zi a fiecarei luni, dupa cum urmeaza: " },
      { text: dateScadente.length > 0 ? dateScadente.join(", ") : "___", bold: true },
      { text: "." },
    ]);

    pdf.article("5.3.", "Plata se va efectua prin transfer bancar in contul Imprumutatorului sau in numerar, cu confirmare scrisa de primire, cu respectarea legislatiei privind plafonul platilor in numerar.");
  } else {
    pdf.pMixed([
      { text: "5.1. ", bold: true },
      { text: "Restituirea integrala a sumei imprumutate de " },
      { text: `${suma > 0 ? suma.toLocaleString("ro-RO") : "___"} ${valuta}`, bold: true },
      { text: " se va face intr-o singura transa, la data scadentei (" },
      { text: dataRestituireFmt, bold: true },
      { text: ")." },
    ]);

    pdf.article("5.2.", "Plata se va efectua prin transfer bancar in contul Imprumutatorului sau in numerar, cu confirmare scrisa de primire, cu respectarea legislatiei privind plafonul platilor in numerar.");
  }

  // VI. PENALITATI
  pdf.section("VI. PENALITATI");

  if (data.penalizareActiva) {
    pdf.pMixed([
      { text: "6.1. ", bold: true },
      { text: "In cazul nerestituirii la termen a sumei imprumutate (sau a ratelor lunare), Imprumutatul datoreaza penalitati de intarziere in cuantum de " },
      { text: `${data.penalizare || "___"}% pe zi`, bold: true },
      { text: " din suma restanta, fara a depasi cuantumul debitului." },
    ]);

    pdf.article("6.2.", "Penalitatile curg de drept, fara punere in intarziere si fara nicio alta formalitate prealabila.");
  } else {
    pdf.article("6.1.", "Partile au convenit ca nu se percep penalitati de intarziere. In cazul nerestituirii la termen, Imprumutatorul poate solicita restituirea sumei pe cale amiabila sau judiciara.");
  }

  // VII. FIDEJUSIUNE (conditional)
  if (hasGarantie) {
    pdf.section("VII. FIDEJUSIUNE");

    pdf.article("7.1.", "Restituirea sumei imprumutate este garantata prin fidejusiunea acordata de:");

    pdf.field("Nume", data.garantNume);
    pdf.field("CNP", data.garantCNP);
    pdf.field("CI", data.garantCI);
    pdf.field("Domiciliu", data.garantAdresa);

    if (data.garantSolidar !== false) {
      pdf.pMixed([
        { text: "7.2. ", bold: true },
        { text: "Fidejusorul se obliga " },
        { text: "solidar", bold: true },
        { text: " cu Imprumutatul sa restituie Imprumutatorului suma imprumutata, dobanzile si penalitatile datorate, in cazul in care Imprumutatul nu isi indeplineste obligatiile la termen." },
      ]);

      pdf.article("7.3.", "In virtutea solidaritatii, Imprumutatorul poate urmari direct Fidejusorul pentru intreaga datorie, fara a fi obligat sa se indrepte mai intai impotriva Imprumutatului.");

      pdf.article("7.4.", "Fidejusorul declara ca a luat cunostinta de continutul prezentului contract si ca isi asuma in mod expres obligatia de garantare.");
    } else {
      pdf.article("7.2.", "Fidejusorul se obliga subsidiar fata de Imprumutat sa restituie Imprumutatorului suma imprumutata, dobanzile si penalitatile datorate, in cazul in care Imprumutatul nu isi indeplineste obligatiile la termen.");

      pdf.article("7.3.", "Fidejusorul declara ca a luat cunostinta de continutul prezentului contract si ca isi asuma in mod expres obligatia de garantare.");
    }
  }

  // OBLIGATIILE PARTILOR
  pdf.section(`${sObligatii}. OBLIGATIILE PARTILOR`);

  pdf.pMixed([{ text: "Obligatiile Imprumutatorului:", bold: true }]);

  pdf.p("- Sa remita Imprumutatului suma convenita, la data si in modalitatea stabilite.", { indent: 8 });
  pdf.p("- Sa nu solicite restituirea sumei inainte de termenul convenit, cu exceptia cazurilor prevazute de lege.", { indent: 8 });

  pdf.pMixed([{ text: "Obligatiile Imprumutatului:", bold: true }]);

  pdf.p("- Sa restituie suma imprumutata la termenul si in conditiile stabilite prin prezentul contract.", { indent: 8 });
  pdf.p("- Scopul imprumutului are caracter declarativ si nu afecteaza obligatia de restituire.", { indent: 8 });
  if (data.tipDobanda !== "fara") {
    pdf.p("- Sa achite dobanda la termenele stabilite, conform art. IV.", { indent: 8 });
  }
  pdf.p("- Sa informeze Imprumutatorul despre orice situatie care ar putea afecta capacitatea de rambursare.", { indent: 8 });

  // INCETAREA CONTRACTULUI
  pdf.section(`${sIncetare}. INCETAREA CONTRACTULUI`);

  pdf.article(`${nIncetare}.1.`, "Contractul inceteaza:");
  pdf.letterItem("a", "prin restituirea integrala a sumei imprumutate si a dobanzilor, daca este cazul");
  pdf.letterItem("b", "la data scadentei");
  pdf.letterItem("c", "prin acordul scris al partilor");
  pdf.letterItem("d", "prin reziliere, in cazul nerespectarii obligatiilor contractuale esentiale");
  pdf.letterItem("e", "prin rambursare anticipata, la initiativa Imprumutatului");

  // LITIGII
  pdf.section(`${sLitigii}. LITIGII`);

  pdf.pMixed([
    { text: `${nLitigii}.1. `, bold: true },
    { text: "Litigiile decurgand din executarea prezentului contract se solutioneaza pe cale amiabila, iar in caz contrar de catre instantele judecatoresti competente." },
  ], { indent: 5 });

  // DISPOZITII FINALE
  pdf.section(`${sFinale}. DISPOZITII FINALE`);

  pdf.pMixed([
    { text: `${nFinale}.1. `, bold: true },
    { text: "Prezentul contract reprezinta un inscris sub semnatura privata si nu constituie titlu executoriu." },
  ], { indent: 5 });

  pdf.pMixed([
    { text: `${nFinale}.2. `, bold: true },
    { text: "Orice modificare a prezentului contract se face prin act aditional semnat de ambele parti." },
  ], { indent: 5 });

  pdf.pMixed([
    { text: `${nFinale}.3. `, bold: true },
    { text: `Contractul se incheie in 2 (doua) exemplare originale, cate unul pentru fiecare parte${data.martori ? ", plus cate un exemplar pentru fiecare martor" : ""}.` },
  ], { indent: 5 });

  // Signatures
  const imprumutatorName = pdf.getPartyNamePFPJ(data, "imprumutator", data.imprumutatorPJ);
  const imprumutatName = pdf.getPartyNamePFPJ(data, "imprumutat", data.imprumutatPJ);

  pdf.signatures(
    { role: "IMPRUMUTATOR", name: imprumutatorName, date: today },
    { role: "IMPRUMUTAT", name: imprumutatName, date: today },
  );

  // Fidejusor signature (conditional)
  if (hasGarantie) {
    pdf.signatureSingle({
      role: "FIDEJUSOR",
      name: data.garantNume || "",
      date: today,
    });
  }

  // Martori signatures (conditional)
  if (data.martori) {
    pdf.signatures(
      { role: "MARTOR 1", name: "________________________", date: "" },
      { role: "MARTOR 2", name: "________________________", date: "" },
    );
  }

  pdf.save(filename);
}
